(() => {
  if (customElements.get('gripb-coupon-wallet')) return;
  const runtime = { busy: false, cart: null, loading: null, states: new Map(), locks: new Map() };
  const normalize = value => typeof value === 'string' ? value.trim().toUpperCase() : '';
  const valid = code => code.length > 0 && code.length <= 255 && !/[,\u0000-\u001f\u007f]/.test(code);
  const wallets = () => document.querySelectorAll('gripb-coupon-wallet');
  const guard = event => {
    if (!runtime.busy) return;
    const target = event.target;
    if (target.closest?.('main-cart') && (event.type === 'submit' || target.closest('quantity-input, [is="cart-remove-item"], [is="cart-discount-remove"], [name="checkout"], a[href*="checkout"]'))) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };
  ['click', 'change', 'submit'].forEach(type => document.addEventListener(type, guard, true));

  class GripbCouponWallet extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };
      this.labels = this.querySelector('[data-coupon-labels]').dataset;
      this.input = this.querySelector('[name="coupon_code"]');
      this.list = this.querySelector('[data-coupon-list]');
      this.status = this.querySelector('[data-coupon-status]');
      this.scope = 'gripb:coupons:v1:' + this.dataset.customer;
      this.stateKey = this.scope + ':' + this.dataset.mode;
      this.issued = new Set(Array.from(this.querySelectorAll('[data-coupon-issued]'), node => normalize(node.dataset.couponIssued)).filter(valid));
      this.applied = new Set(Array.from(this.querySelectorAll('[data-coupon-applied]'), node => normalize(node.dataset.couponApplied)).filter(valid));
      this.rejected = new Set();
      this.saved = this.readSaved();
      this.selected = new Set(this.applied);
      this.message = '';
      this.restoreState(runtime.states.get(this.stateKey));
      this.querySelector('[data-coupon-controls]').hidden = false;
      this.querySelector('[data-coupon-register]').addEventListener('submit', event => {
        event.preventDefault(); this.register();
      }, opts);
      this.querySelector('[data-coupon-apply]')?.addEventListener('click', () => this.apply([...this.selected]), opts);
      this.querySelector('[data-coupon-clear]')?.addEventListener('click', () => this.apply([]), opts);
      this.list.addEventListener('change', event => {
        if (!event.target.matches('[data-coupon-select]')) return;
        const code = event.target.value;
        event.target.checked ? this.selected.add(code) : this.selected.delete(code);
        this.say('selectionNote'); this.captureState();
      }, opts);
      this.list.addEventListener('click', event => {
        const button = event.target.closest('[data-coupon-forget]');
        if (!button) return;
        const code = button.dataset.couponForget;
        this.saved = this.saved.filter(item => item !== code);
        if (!this.applied.has(code)) this.selected.delete(code);
        this.persist(); this.render(); this.captureState();
      }, opts);
      this.input.addEventListener('input', () => this.captureState(), opts);
      this.querySelector('details').addEventListener('toggle', () => this.captureState(), opts);
      document.addEventListener('cart:updated', event => {
        if (!event.detail?.cart) return;
        runtime.cart = event.detail.cart;
        this.acceptCart(event.detail.cart);
      }, opts);
      window.addEventListener('storage', event => {
        if (event.key !== this.scope) return;
        this.saved = this.readSaved(); this.render();
      }, opts);
      this.render();
      if (runtime.cart) this.acceptCart(runtime.cart);
      else this.loadCart();
    }

    disconnectedCallback() {
      this.captureState();
      this.controller?.abort();
      this.controller = null;
    }

    readSaved() {
      try {
        const values = JSON.parse(localStorage.getItem(this.scope) || '[]');
        return Array.isArray(values) ? [...new Set(values.map(normalize).filter(valid))].slice(0, 100) : [];
      } catch (_) { return []; }
    }

    persist() {
      try { localStorage.setItem(this.scope, JSON.stringify(this.saved)); return true; }
      catch (_) { this.say('storage'); return false; }
    }

    captureState() {
      if (!this.input) return null;
      const state = { selected: [...this.selected], rejected: [...this.rejected], input: this.input.value, open: this.querySelector('details').open, message: this.message };
      runtime.states.set(this.stateKey, state);
      return state;
    }

    restoreState(state) {
      if (!state || !this.input) return;
      this.selected = new Set(state.selected || []);
      this.rejected = new Set(state.rejected || []);
      this.input.value = state.input || '';
      this.querySelector('details').open = state.open !== false;
      this.message = state.message || '';
      this.render();
    }

    say(key) {
      this.message = this.labels[key] || key;
      this.status.textContent = this.message;
      this.captureState();
    }

    register() {
      if (runtime.busy) return;
      const code = normalize(this.input.value);
      if (!valid(code)) { this.say('invalid'); this.input.focus(); return; }
      if (this.saved.includes(code) || this.issued.has(code)) this.say('duplicate');
      else {
        this.saved = [code, ...this.saved].slice(0, 100);
        if (this.persist()) this.say('registered');
      }
      this.selected.add(code);
      this.input.value = '';
      this.render(); this.captureState();
    }

    render() {
      const codes = [...new Set([...this.issued, ...this.saved, ...this.applied])];
      const fragment = document.createDocumentFragment();
      for (const code of codes) {
        const item = document.createElement('li');
        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox'; input.value = code; input.dataset.couponSelect = '';
        input.checked = this.selected.has(code); input.disabled = runtime.busy;
        const text = document.createElement('span');
        const title = document.createElement('strong'); title.textContent = code;
        const note = document.createElement('small');
        note.textContent = this.applied.has(code) ? this.labels.applied : this.rejected.has(code) ? this.labels.unavailable : this.issued.has(code) ? this.labels.issued : this.labels.saved;
        if (this.applied.has(code)) item.dataset.applied = '';
        if (this.rejected.has(code)) item.dataset.rejected = '';
        text.append(title, note); label.append(input, text); item.append(label);
        if (this.saved.includes(code) && !this.issued.has(code) && !this.applied.has(code)) {
          const remove = document.createElement('button');
          remove.type = 'button'; remove.dataset.couponForget = code; remove.textContent = '×';
          remove.setAttribute('aria-label', code + ' ' + this.labels.remove);
          remove.disabled = runtime.busy; item.append(remove);
        }
        fragment.append(item);
      }
      this.list.replaceChildren(fragment);
      this.querySelector('[data-coupon-count]').textContent = String(codes.length);
      this.querySelector('[data-coupon-empty]').hidden = codes.length > 0;
      this.status.textContent = this.message;
      this.setAttribute('aria-busy', String(runtime.busy));
      this.querySelectorAll('button, input').forEach(node => { node.disabled = runtime.busy; });
      const clear = this.querySelector('[data-coupon-clear]');
      if (clear) clear.hidden = this.applied.size === 0 && this.rejected.size === 0;
    }

    async loadCart() {
      if (!runtime.loading) {
        runtime.loading = fetch(this.dataset.root + 'cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
          .then(response => { if (!response.ok) throw new Error('Cart unavailable'); return response.json(); })
          .then(cart => { if (!runtime.cart && !runtime.busy) runtime.cart = cart; })
          .catch(() => {}).finally(() => { runtime.loading = null; });
      }
      await runtime.loading;
      if (this.isConnected && runtime.cart) this.acceptCart(runtime.cart);
    }

    acceptCart(cart) {
      if (!Array.isArray(cart.discount_codes)) return;
      const previous = this.applied;
      this.applied = new Set(cart.discount_codes.filter(item => item.applicable).map(item => normalize(item.code)));
      this.rejected = new Set(cart.discount_codes.filter(item => !item.applicable).map(item => normalize(item.code)));
      // Preserve unsent selections, but do not reselect a code the customer has removed.
      previous.forEach(code => { if (!this.applied.has(code)) this.selected.delete(code); });
      this.applied.forEach(code => this.selected.add(code));
      this.render(); this.captureState();
    }

    async apply(codes) {
      const mainCart = this.closest('main-cart');
      if (runtime.busy || (mainCart?.gripbPendingQuantityUpdates || 0) > 0) { this.say('wait'); return; }
      if (codes.length > 6) { this.say('limit'); return; }
      if (!codes.length && !this.applied.size && !this.rejected.size) { this.say('choose'); return; }
      if (!mainCart || !window.FoxTheme?.pubsub) { this.say('error'); return; }
      runtime.busy = true;
      mainCart.gripbPendingQuantityUpdates = (mainCart.gripbPendingQuantityUpdates || 0) + 1;
      mainCart.gripbQuantityRevision = (mainCart.gripbQuantityRevision || 0) + 1;
      mainCart.setAttribute('data-gripb-coupon-busy', '');
      mainCart.querySelectorAll('quantity-input button, quantity-input input, [name="checkout"]').forEach(node => {
        runtime.locks.set(node, node.disabled); node.disabled = true;
      });
      this.say('busy'); wallets().forEach(wallet => wallet.render());
      const sectionId = mainCart.id.replace('MainCart-', '');
      const sections = [sectionId];
      document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }));
      const bundle = [...new Set(sections)].slice(0, 5);
      const note = mainCart.querySelector('[name="note"]')?.value;
      let updated = false;
      try {
        const response = await fetch(this.dataset.root + 'cart/update.js', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(','), sections: bundle, sections_url: this.dataset.cartUrl, ...(note !== undefined ? { note } : {}) })
        });
        const cart = await response.json();
        if (!response.ok || cart.errors || !Array.isArray(cart.items)) throw new Error('Coupon update failed');
        updated = true;
        if (!cart.sections?.[sectionId]) {
          const rendered = await fetch(this.dataset.cartUrl + '?sections=' + encodeURIComponent(bundle.join(',')), { credentials: 'same-origin', cache: 'no-store' });
          if (!rendered.ok) throw new Error('Section update failed');
          cart.sections = await rendered.json();
          if (!cart.sections[sectionId]) throw new Error('Cart section unavailable');
        }
        runtime.cart = cart;
        this.acceptCart(cart);
        // A rejected code is still selectable so its owner can check conditions and retry.
        codes.forEach(code => { if (this.saved.includes(code) || this.issued.has(code)) this.selected.add(code); });
        const accepted = new Set((cart.discount_codes || []).filter(item => item.applicable).map(item => normalize(item.code)));
        this.say(!codes.length ? 'cleared' : codes.some(code => !accepted.has(code)) ? 'rejected' : 'success');
        this.captureState();
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'gripb-coupons', cart });
      } catch (_) {
        this.say('error');
        // If the server changed the cart but section rendering failed, load its authoritative totals.
        if (updated) window.location.reload();
      } finally {
        runtime.busy = false;
        mainCart.gripbPendingQuantityUpdates = Math.max(0, mainCart.gripbPendingQuantityUpdates - 1);
        mainCart.removeAttribute('data-gripb-coupon-busy');
        runtime.locks.forEach((disabled, node) => { if (node.isConnected) node.disabled = disabled; });
        runtime.locks.clear();
        wallets().forEach(wallet => wallet.render());
      }
    }
  }
  customElements.define('gripb-coupon-wallet', GripbCouponWallet);
})();
